/**
 * Mobile Roster Column Configurations
 * Defines columns for roster management with Move/Drop actions.
 *
 * Bible § 14 stat-column rule (ratified 2026-05-12): stat columns are derived
 * from league_settings.scoring_categories via statConfigs (built by
 * utils/statMapping.getStatConfigs). Only context (G/AB for batters, G/GS for
 * pitchers), position, name, team, and action/financial columns are static.
 */

import React from 'react';
import { Move, Trash2, ArrowLeftRight } from 'lucide-react';
import { dynastyTheme } from '../../../services/colorService';
import { formatContractDisplay } from '../../../services/contractUtils';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import { buildDynamicStatColumn } from '../../../utils/dynamicStatColumn';
import { statFieldSimple } from '../../../utils/statColumnDedup';
import MobilePlayerLink from './MobilePlayerLink';
import MobileInjuryBadge from './MobileInjuryBadge';
import { isTwoWayPlayer } from '../../../utils/playerClassification';

// "TWP" pill next to a two-way player's name (Bible §4.5.9.1 bench rule):
// a reserve TWP is listed on the hitting side; the badge signals dual identity.
const MobileTwoWayBadge = () => (
  <span
    style={{
      fontSize: '7px', fontWeight: 700, padding: '1px 3px', marginLeft: '4px',
      borderRadius: '3px', lineHeight: 1.2, flexShrink: 0,
      background: dynastyTheme.tokens.colors.primary, color: '#000',
    }}
    title="Two-way player (pitcher + hitter)"
  >
    TWP
  </span>
);

const C = dynastyTheme.tokens.colors;

// =============================================================================
// MOBILE BATTERS ROSTER COLUMNS (with Actions)
// =============================================================================
export const createMobileBattersRosterColumns = (callbacks = {}) => {
  const {
    onMoveClick,
    onDropClick,
    onTradeClick,
    onPlayerClick,
    statConfigs = [],
  } = callbacks;
  
  return [
    // Position Slot (Leftmost)
    {
      key: 'position_slot',
      header: 'POS',
      width: '32px',
      minWidth: '32px',
      maxWidth: '32px',
      textAlign: 'center',
      sticky: true,
      fontSize: '9px',
      accessor: (row) => row.slotPosition || '-',
      render: (value, row) => {
        // Only show position on rolling row (row 2) - YELLOW and BOLD
        if (row.statType === 'rolling') {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.primary,
              fontWeight: '700'
            }}>
              {value}
            </span>
          );
        }
        // Hide on other rows
        return null;
      }
    },
    
    // Player Name (Sticky, Clickable)
    {
      key: 'player_name',
      header: 'Player',
      width: '90px',
      minWidth: '90px',
      maxWidth: '90px',
      textAlign: 'left',
      sticky: true,
      nowrap: true,
      ellipsis: true,
      accessor: (row) => row.player?.info?.full_name || row.info?.full_name || row.playerData?.info?.full_name,
      render: (value, row) => {
        // Season row - show player name (clickable)
        if (row.statType === 'season') {
          const mlbId = row.player?.ids?.mlb || row.ids?.mlb;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
              <MobilePlayerLink
                mlbId={mlbId}
                name={value}
                onClick={onPlayerClick ? (id) => onPlayerClick(id) : null}
                style={{ fontWeight: 500, fontSize: 'inherit' }}
              />
              {isTwoWayPlayer(row.player || row) && <MobileTwoWayBadge />}
              <MobileInjuryBadge mlbId={mlbId} />
            </span>
          );
        }
        // Rolling row - show "14-Day" label (no lightning bolt)
        if (row.statType === 'rolling') {
          return (
            <span style={{ 
              fontSize: '10px',
              color: dynastyTheme.tokens.colors.warning 
            }}>
              14-Day
            </span>
          );
        }
        // Accrued row - show "Accrued" label (no lightning bolt)
        if (row.statType === 'accrued') {
          return (
            <span style={{ 
              fontSize: '10px',
              color: dynastyTheme.tokens.colors.success 
            }}>
              Accrued
            </span>
          );
        }
        // Fallback
        return value;
      }
    },
    
    // Team
    {
      key: 'team',
      header: 'Tm',
      width: '32px',
      textAlign: 'center',
      fontSize: '9px',
      accessor: (row) => row.player?.info?.mlb_team || row.info?.mlb_team || row.playerData?.info?.mlb_team || '-',
      render: (value, row) => {
        // Only show team on rolling row (row 2) - WHITE (not bold)
        if (row.statType === 'rolling') {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white
            }}>
              {value}
            </span>
          );
        }
        // Hide on other rows
        return null;
      }
    },
    
    // Games
    {
      key: 'games',
      header: 'G',
      width: '24px',
      accessor: (row) => {
        if (row.isTotalsRow) {
          return row.totals?.games_played;
        }
        if (row.statType === 'season') {
          return row.player?.stats?.season?.hitting?.games_played || 0;
        }
        if (row.statType === 'rolling') {
          return row.player?.stats?.rolling_14_day?.hitting?.games_played || 0;
        }
        // Default to accrued (active_accrued)
        return row.player?.stats?.active_accrued?.hitting?.games_played || 0;
      }
    },
    
    // At Bats
    {
      key: 'ab',
      header: 'AB',
      width: '28px',
      accessor: (row) => {
        if (row.isTotalsRow) {
          return row.totals?.at_bats;
        }
        if (row.statType === 'season') {
          return row.player?.stats?.season?.hitting?.at_bats || 0;
        }
        if (row.statType === 'rolling') {
          return row.player?.stats?.rolling_14_day?.hitting?.at_bats || 0;
        }
        // Default to accrued (active_accrued)
        return row.player?.stats?.active_accrued?.hitting?.at_bats || 0;
      }
    },
    
    // === Dynamic scoring-category columns (driven by league_settings.scoring_categories) ===
    // DD-STAT-CONSOLIDATION sweep: exclude dynamic G/AB — static columns render them.
    ...statConfigs
      .filter((c) => c.category === 'hitting' && !['games_played', 'at_bats'].includes(statFieldSimple(c)))
      .map(buildDynamicStatColumn),

    // Price
    {
      key: 'price',
      header: 'Price',
      width: '40px',
      textAlign: 'center',
      fontSize: '10px',
      accessor: (row) => {
        const player = row.player || row;
        return player.league_context?.financial?.baseline_price;
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.warning }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      }
    },
    
    // Salary
    {
      key: 'salary',
      header: 'Salary',
      width: '40px',
      textAlign: 'center',
      fontSize: '10px',
      accessor: (row) => {
        const player = row.player || row;
        return player?.league_context?.financial?.contract_salary || player?.financial?.contract_salary || 0;
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.success }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      }
    },
    
    // Contract
    {
      key: 'contract',
      header: 'Contract',
      width: '50px',
      textAlign: 'center',
      fontSize: '10px',
      accessor: (row) => {
        const player = row.player || row;
        const fin = player?.league_context?.financial || player?.financial || {};
        return formatContractDisplay(fin.contract_type, fin.contract_years);
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          return (
            <span style={{ color: value === '\u2014' ? C.coolGray[680] : C.white }}>
              {value}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      }
    },
    
    // Actions (Move, Drop, Trade) - Show on rolling row only
    {
      key: 'actions',
      header: 'Actions',
      width: '90px',
      textAlign: 'center',
      accessor: (row) => row,
      render: (value, row) => {
        // Only show on rolling row (2nd row)
        if (row.statType !== 'rolling') return null;
        
        const player = row.player || row;
        if (!player) return null;
        
        return (
          <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', alignItems: 'center' }}>
            {onMoveClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveClick(player);
                }}
                style={{
                  padding: '3px',
                  backgroundColor: dynastyTheme.tokens.colors.info + '33',
                  color: dynastyTheme.tokens.colors.info,
                  border: 'none',
                  borderRadius: dynastyTheme.tokens.radius.base,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                title="Move player"
              >
                <Move style={{ width: '12px', height: '12px' }} />
              </button>
            )}
            {onDropClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDropClick(player);
                }}
                style={{
                  padding: '3px',
                  backgroundColor: dynastyTheme.tokens.colors.error + '33',
                  color: dynastyTheme.tokens.colors.error,
                  border: 'none',
                  borderRadius: dynastyTheme.tokens.radius.base,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                title="Drop player"
              >
                <Trash2 style={{ width: '12px', height: '12px' }} />
              </button>
            )}
            {onTradeClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTradeClick(player);
                }}
                style={{
                  padding: '3px',
                  backgroundColor: dynastyTheme.tokens.colors.purple + '33',
                  color: dynastyTheme.tokens.colors.purple,
                  border: 'none',
                  borderRadius: dynastyTheme.tokens.radius.base,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                title="Trade player"
              >
                <ArrowLeftRight style={{ width: '12px', height: '12px' }} />
              </button>
            )}
          </div>
        );
      }
    }
  ];
};

// =============================================================================
// MOBILE PITCHERS ROSTER COLUMNS (with Actions)
// =============================================================================
export const createMobilePitchersRosterColumns = (callbacks = {}) => {
  const {
    onMoveClick,
    onDropClick,
    onTradeClick,
    onPlayerClick,
    statConfigs = [],
  } = callbacks;
  
  return [
    // Position Slot (Leftmost)
    {
      key: 'position_slot',
      header: 'POS',
      width: '32px',
      minWidth: '32px',
      maxWidth: '32px',
      textAlign: 'center',
      sticky: true,
      fontSize: '9px',
      accessor: (row) => row.slotPosition || '-',
      render: (value, row) => {
        // Only show position on rolling row (row 2) - YELLOW and BOLD
        if (row.statType === 'rolling') {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.primary,
              fontWeight: '700'
            }}>
              {value}
            </span>
          );
        }
        // Hide on other rows
        return null;
      }
    },
    
    // Player Name (Sticky, Clickable)
    {
      key: 'player_name',
      header: 'Player',
      width: '90px',
      minWidth: '90px',
      maxWidth: '90px',
      textAlign: 'left',
      sticky: true,
      nowrap: true,
      ellipsis: true,
      accessor: (row) => row.player?.info?.full_name || row.info?.full_name || row.playerData?.info?.full_name,
      render: (value, row) => {
        // Season row - show player name (clickable)
        if (row.statType === 'season') {
          const mlbId = row.player?.ids?.mlb || row.ids?.mlb;
          return (
            <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
              <MobilePlayerLink
                mlbId={mlbId}
                name={value}
                onClick={onPlayerClick ? (id) => onPlayerClick(id) : null}
                style={{ fontWeight: 500, fontSize: 'inherit' }}
              />
              {isTwoWayPlayer(row.player || row) && <MobileTwoWayBadge />}
              <MobileInjuryBadge mlbId={mlbId} />
            </span>
          );
        }
        // Rolling row - show "14-Day" label (no lightning bolt)
        if (row.statType === 'rolling') {
          return (
            <span style={{ 
              fontSize: '10px',
              color: dynastyTheme.tokens.colors.warning 
            }}>
              14-Day
            </span>
          );
        }
        // Accrued row - show "Accrued" label (no lightning bolt)
        if (row.statType === 'accrued') {
          return (
            <span style={{ 
              fontSize: '10px',
              color: dynastyTheme.tokens.colors.success 
            }}>
              Accrued
            </span>
          );
        }
        // Fallback
        return value;
      }
    },
    
    // Team
    {
      key: 'team',
      header: 'Tm',
      width: '32px',
      textAlign: 'center',
      fontSize: '9px',
      accessor: (row) => row.player?.info?.mlb_team || row.info?.mlb_team || row.playerData?.info?.mlb_team || '-',
      render: (value, row) => {
        // Only show team on rolling row (row 2) - WHITE (not bold)
        if (row.statType === 'rolling') {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white
            }}>
              {value}
            </span>
          );
        }
        // Hide on other rows
        return null;
      }
    },
    
    // Games
    {
      key: 'games',
      header: 'G',
      width: '24px',
      accessor: (row) => {
        if (row.isTotalsRow) {
          return row.totals?.games_played;
        }
        if (row.statType === 'season') {
          return row.player?.stats?.season?.pitching?.games_played || 0;
        }
        if (row.statType === 'rolling') {
          return row.player?.stats?.rolling_14_day?.pitching?.games_played || 0;
        }
        return row.player?.stats?.active_accrued?.pitching?.games_played || 0;
      }
    },
    
    // Games Started
    {
      key: 'gs',
      header: 'GS',
      width: '24px',
      accessor: (row) => {
        if (row.isTotalsRow) {
          return row.totals?.games_started;
        }
        if (row.statType === 'season') {
          return row.player?.stats?.season?.pitching?.games_started || 0;
        }
        if (row.statType === 'rolling') {
          return row.player?.stats?.rolling_14_day?.pitching?.games_started || 0;
        }
        return row.player?.stats?.active_accrued?.pitching?.games_started || 0;
      }
    },
    
    // === Dynamic scoring-category columns (driven by league_settings.scoring_categories) ===
    // DD-STAT-CONSOLIDATION sweep: exclude dynamic G/GS — static columns render them.
    ...statConfigs
      .filter((c) => c.category === 'pitching' && !['games_played', 'games_started'].includes(statFieldSimple(c)))
      .map(buildDynamicStatColumn),

    // Price
    {
      key: 'price',
      header: 'Price',
      width: '40px',
      textAlign: 'center',
      fontSize: '10px',
      accessor: (row) => {
        const player = row.player || row;
        return player.league_context?.financial?.baseline_price;
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.warning }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      }
    },
    
    // Salary
    {
      key: 'salary',
      header: 'Salary',
      width: '40px',
      textAlign: 'center',
      fontSize: '10px',
      accessor: (row) => {
        const player = row.player || row;
        return player?.league_context?.financial?.contract_salary || player?.financial?.contract_salary || 0;
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.success }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      }
    },
    
    // Contract
    {
      key: 'contract',
      header: 'Contract',
      width: '50px',
      textAlign: 'center',
      fontSize: '10px',
      accessor: (row) => {
        const player = row.player || row;
        const fin = player?.league_context?.financial || player?.financial || {};
        return formatContractDisplay(fin.contract_type, fin.contract_years);
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          return (
            <span style={{ color: value === '\u2014' ? C.coolGray[680] : C.white }}>
              {value}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      }
    },
    
    // Actions (Move, Drop, Trade) - Show on rolling row only
    {
      key: 'actions',
      header: 'Actions',
      width: '90px',
      textAlign: 'center',
      accessor: (row) => row,
      render: (value, row) => {
        // Only show on rolling row (2nd row)
        if (row.statType !== 'rolling') return null;
        
        const player = row.player || row;
        if (!player) return null;
        
        return (
          <div style={{ display: 'flex', gap: '3px', justifyContent: 'center', alignItems: 'center' }}>
            {onMoveClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveClick(player);
                }}
                style={{
                  padding: '3px',
                  backgroundColor: dynastyTheme.tokens.colors.info + '33',
                  color: dynastyTheme.tokens.colors.info,
                  border: 'none',
                  borderRadius: dynastyTheme.tokens.radius.base,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                title="Move player"
              >
                <Move style={{ width: '12px', height: '12px' }} />
              </button>
            )}
            {onDropClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDropClick(player);
                }}
                style={{
                  padding: '3px',
                  backgroundColor: dynastyTheme.tokens.colors.error + '33',
                  color: dynastyTheme.tokens.colors.error,
                  border: 'none',
                  borderRadius: dynastyTheme.tokens.radius.base,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                title="Drop player"
              >
                <Trash2 style={{ width: '12px', height: '12px' }} />
              </button>
            )}
            {onTradeClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTradeClick(player);
                }}
                style={{
                  padding: '3px',
                  backgroundColor: dynastyTheme.tokens.colors.purple + '33',
                  color: dynastyTheme.tokens.colors.purple,
                  border: 'none',
                  borderRadius: dynastyTheme.tokens.radius.base,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background-color 0.2s'
                }}
                title="Trade player"
              >
                <ArrowLeftRight style={{ width: '12px', height: '12px' }} />
              </button>
            )}
          </div>
        );
      }
    }
  ];
};
